import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Save, ArrowLeft, X, Plus } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { HtmlContentUploader } from '../components/HtmlContentUploader';
import { Button } from '../../components';
import { projectsService } from '../../services';

const FormContainer = styled.form`
  max-width: 800px;
  margin: 0 auto;
`;

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;

  label {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 0.5rem;
    font-size: ${props => props.theme.typography.small};
  }

  input, textarea, select {
    padding: 0.75rem 1rem;
    border: 1px solid ${props => props.theme.colors.border.medium};
    border-radius: ${props => props.theme.borderRadius.md};
    font-size: ${props => props.theme.typography.body};
    font-family: inherit;
    background-color: ${props => props.theme.colors.background.primary};
    color: ${props => props.theme.colors.text.primary};
    transition: border-color ${props => props.theme.transitions.fast};

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }

    &::placeholder {
      color: ${props => props.theme.colors.text.tertiary};
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }

  &.full-width {
    grid-column: 1 / -1;
  }
`;


const ImagePreview = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  margin-bottom: 1rem;

  .remove-btn {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: rgba(239, 68, 68, 0.9);
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${props => props.theme.colors.primaryLight};
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.small};

  button {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
  }
`;

const TagInput = styled.div`
  display: flex;
  gap: 0.5rem;

  input {
    flex: 1;
  }
`;


const ToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-weight: 500;
    color: ${props => props.theme.colors.text.primary};
  }

  input[type="checkbox"] {
    width: auto;
    margin: 0;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.theme.colors.border.light};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const ContentSizeIndicator = styled.div`
  margin-top: 0.5rem;
  font-size: ${props => props.theme.typography.small};
  color: ${props => props.$warning ? '#f59e0b' : props.theme.colors.text.tertiary};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.$warning && `
    font-weight: 500;
  `}
`;

export const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    myRole: '',
    year: '',
    content: '',
    technologies: [],
    featuredImage: '',
    isProtected: false,
    passcode: ''
  });

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const fetchProject = async () => {
    try {
      const project = await projectsService.getProjectById(id);
      setFormData({
        title: project.title || '',
        description: project.description || '',
        myRole: project.myRole || '',
        year: project.year || '',
        content: project.content || project.longDescription || '',
        technologies: project.technologies || [],
        featuredImage: project.featuredImage || '',
        isProtected: project.isProtected || false,
        passcode: project.passcode || ''
      });
    } catch (error) {
      console.error('Error fetching project:', error);
      alert('Failed to load project');
      navigate('/admin/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      featuredImage: ''
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.technologies.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        technologies: [...prev.technologies, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      technologies: prev.technologies.filter(t => t !== tag)
    }));
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert('Please enter a project title');
      return;
    }

    // Check content size (Firestore limit is ~1MB per field)
    const contentSize = new Blob([formData.content]).size;
    const maxSize = 1000000; // ~1MB (leaving some margin)

    if (contentSize > maxSize) {
      alert(
        `Content is too large (${(contentSize / 1024 / 1024).toFixed(2)}MB). ` +
        `Maximum size is ~1MB. Please reduce the number of images or use smaller images.\n\n` +
        `Tip: Make sure you're uploading images using the image button, not pasting them directly.`
      );
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await projectsService.updateProject(id, formData);
        alert('Project updated successfully!');
      } else {
        const newId = await projectsService.createProject(formData);
        alert('Project created successfully!');
        navigate(`/admin/projects/${newId}/edit`);
      }
    } catch (error) {
      console.error('Error saving project:', error);

      if (error.message?.includes('longer than')) {
        alert(
          'Content is too large for Firestore. Please reduce the number of images or use smaller images.\n\n' +
          'Make sure you are uploading images using the image button (not pasting), so they are stored in Firebase Storage.'
        );
      } else {
        alert('Failed to save project: ' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEditing ? 'Edit Project' : 'New Project'}>
        <LoadingContainer>
          <p>Loading project...</p>
        </LoadingContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? 'Edit Project' : 'New Project'}>
      <FormContainer onSubmit={handleSubmit}>
        <FormHeader>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/admin/projects')}
          >
            <ArrowLeft size={20} />
            Back to Projects
          </Button>
        </FormHeader>

        <FormGrid>
          <FormGroup>
            <label htmlFor="title">Project Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter project title"
              required
            />
          </FormGroup>


          <FormGroup className="full-width">
            <label htmlFor="description">Short Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description for project cards"
              required
            />
          </FormGroup>
        </FormGrid>

        <FormGroup>
          <label htmlFor="myRole">My Role</label>
          <input
            type="text"
            id="myRole"
            name="myRole"
            value={formData.myRole}
            onChange={handleChange}
            placeholder="e.g. Product Designer, Lead UX Designer, Frontend Developer"
          />
        </FormGroup>

        <FormGroup>
          <label htmlFor="year">Year</label>
          <input
            type="text"
            id="year"
            name="year"
            value={formData.year}
            onChange={handleChange}
            placeholder="e.g. 2024"
          />
        </FormGroup>

        <FormGroup>
          <label>Featured Image URL</label>
          <input
            type="url"
            name="featuredImage"
            value={formData.featuredImage}
            onChange={handleChange}
            placeholder="Paste image URL (e.g. https://example.com/image.jpg)"
          />
          {formData.featuredImage && (
            <ImagePreview image={formData.featuredImage}>
              <button
                type="button"
                className="remove-btn"
                onClick={removeImage}
              >
                <X size={16} />
              </button>
            </ImagePreview>
          )}
        </FormGroup>

        <FormGroup>
          <label>Technologies/Tags</label>
          <TagsContainer>
            {formData.technologies.map((tag, index) => (
              <Tag key={index}>
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                >
                  <X size={14} />
                </button>
              </Tag>
            ))}
          </TagsContainer>
          <TagInput>
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={handleTagKeyPress}
              placeholder="Add technology or tag (e.g., React, UI Design, Figma)"
            />
            <Button
              type="button"
              onClick={addTag}
              variant="outline"
              size="sm"
            >
              <Plus size={16} />
              Add
            </Button>
          </TagInput>
        </FormGroup>

        <FormGroup>
          <label>Protection Settings</label>
          <ToggleGroup>
            <label>
              <input
                type="checkbox"
                checked={formData.isProtected}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  isProtected: e.target.checked,
                  passcode: e.target.checked ? prev.passcode : ''
                }))}
              />
              Protect this project with passcode
            </label>
          </ToggleGroup>
          {formData.isProtected && (
            <input
              type="password"
              name="passcode"
              value={formData.passcode}
              onChange={handleChange}
              placeholder="Enter passcode for this project"
              required
            />
          )}
        </FormGroup>

        <FormGroup>
          <label>Project Content</label>
          <HtmlContentUploader
            value={formData.content}
            onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
            placeholder="Upload your Google Doc exported as HTML..."
          />
          <ContentSizeIndicator $warning={new Blob([formData.content]).size > 800000}>
            Content size: {(new Blob([formData.content]).size / 1024).toFixed(1)} KB
            {new Blob([formData.content]).size > 800000 && ' (Warning: approaching 1MB limit)'}
          </ContentSizeIndicator>
        </FormGroup>

        <FormActions>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/projects')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            <Save size={20} />
            {saving ? 'Saving...' : (isEditing ? 'Update Project' : 'Create Project')}
          </Button>
        </FormActions>
      </FormContainer>
    </AdminLayout>
  );
};